import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../App';
import Migally from '../assets/images/background01.png';

function Home() {
  const [expertise, setExpertise] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [showExpertise, setShowExpertise] = useState(false);
  const expertiseRef = useRef(null);

  useEffect(() => {
    async function fetchExpertise() {
      try {
        setLoading(true);
        const { data } = await supabase.from('expertise').select('*').order('id', { ascending: true });
        if (data && data.length > 0) setExpertise(data);
      } catch (e) {
        setExpertise([
          { id: 1, title: "Software Development", description: "Custom software solutions tailored to your unique business needs.", bg_image: "/images/placeholder1.png" },
          { id: 2, title: "Network Solutions", description: "Design, implementation, and management of secure infrastructures.", bg_image: "/images/placeholder2.png" },
          { id: 3, title: "Cloud Services", description: "Migration, management, and optimization of cloud infrastructure.", bg_image: "/images/placeholder3.png" }
        ]);
      } finally {
        setLoading(false);
      }
    }
    if (supabase) fetchExpertise();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('opacity-0', 'translate-y-12');
            entry.target.classList.add('opacity-100', 'translate-y-0');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    const hiddenElements = document.querySelectorAll('.animate-on-scroll');
    hiddenElements.forEach((el) => observer.observe(el));

    return () => {
      hiddenElements.forEach((el) => observer.unobserve(el));
    };
  }, [expertise, loading, showExpertise]);

  const toggleExpertise = () => {
    setShowExpertise(!showExpertise);
    
    if (!showExpertise) {
      setTimeout(() => {
        expertiseRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <section className="relative w-full min-h-screen flex items-center justify-center pt-24 pb-12 overflow-hidden">
        <div className="absolute inset-0 bg-slate-950/60 z-0 border-b border-blue-900/20"></div>
        <div className="relative z-10 w-full max-w-7xl px-6 flex flex-col md:flex-row items-center justify-between gap-12">
          <div className="md:w-1/2 animate-on-scroll opacity-0 translate-y-12 transition-all duration-[1000ms] ease-out text-center md:text-left">
            <h1 className="text-4xl md:text-7xl font-black text-white mb-4 tracking-tighter leading-tight drop-shadow-lg">
              Welcome to <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-600">
                iLAMI Technologies.
              </span>
            </h1>
            <p className="text-lg md:text-xl text-slate-200 mb-4 font-bold tracking-tight">
              Your trusted partner in innovative IT solutions, driving digital transformation.
            </p>
            <p className="text-sm md:text-base text-slate-400 mb-8 font-medium max-w-2xl leading-relaxed">
              At iLAMI Technologies, we're dedicated to delivering cutting-edge technology services that empower businesses to thrive. From custom software development to robust networking solutions, we're here to help you achieve your goals with unparalleled expertise and commitment.
            </p>
            <div className="flex flex-row gap-4 justify-center md:justify-start">
              <Link to="/services" className="bg-blue-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-blue-500 hover:shadow-[0_0_20px_rgba(37,99,235,0.4)] active:scale-95 transition-all duration-300 text-sm">
                Our Services
              </Link>
              <Link to="/contact" className="backdrop-blur-md bg-white/5 text-white px-8 py-3 rounded-lg font-bold hover:bg-white/10 hover:border-blue-400/50 active:scale-95 transition-all duration-300 text-sm border border-slate-700">
                Contact Us
              </Link>
            </div>
          </div>
          <div className="md:w-1/2 flex justify-center items-center relative animate-on-scroll opacity-0 translate-y-12 transition-all duration-[1200ms] delay-300 ease-out group">
            <div className="absolute w-[110%] h-[110%] bg-blue-600/10 blur-[100px] rounded-full"></div>
            {/* UPDATED: Changed w-80 h-80 to w-64 h-64 for smaller screens */}
            <div className="relative w-64 h-64 md:w-[480px] md:h-[480px] flex items-center justify-center p-1 rotate-90">
              <div 
                className="absolute inset-0 bg-gradient-to-br from-blue-500 via-transparent to-blue-400 opacity-40 shadow-[0_0_40px_rgba(37,99,235,0.3)]"
                style={{ clipPath: 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)' }}
              ></div>
              <div 
                className="relative w-full h-full overflow-hidden bg-white border-2 border-slate-800"
                style={{ clipPath: 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)' }}
              >
                <img 
                  src={Migally}
                  alt="iLAMI Logo" 
                  className="absolute inset-0 w-full h-full object-contain -rotate-90 scale-[0.80] transition-transform duration-1000 group-hover:scale-[0.85]"
                />
              </div>
              <div className="absolute -top-4 -right-4 w-24 h-24 border-t-2 border-r-2 border-blue-500/40 rounded-tr-3xl"></div>
              <div className="absolute -bottom-4 -left-4 w-24 h-24 border-b-2 border-l-2 border-blue-500/40 rounded-bl-3xl"></div>
            </div>
          </div>
        </div>
        <button 
          onClick={toggleExpertise}
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2 flex flex-col items-center gap-2 group focus:outline-none z-20"
        >
          <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-slate-400 group-hover:text-blue-400 transition-colors duration-300">
            {showExpertise ? 'Close Expertise' : 'Explore Expertise'}
          </span>
          <div className={`p-2 rounded-full border border-slate-700 group-hover:border-blue-500 bg-slate-900/50 backdrop-blur-md group-hover:shadow-[0_0_15px_rgba(37,99,235,0.5)] transition-all duration-500 ${showExpertise ? 'rotate-180' : 'animate-bounce'}`}>
            <svg className="w-5 h-5 text-slate-300 group-hover:text-blue-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
            </svg>
          </div>
        </button>
      </section>
      <div 
        ref={expertiseRef}
        className={`w-full transition-all duration-1000 ease-in-out overflow-hidden ${
          showExpertise ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <section className="py-16 px-6 relative z-10">
          <div className="absolute inset-0 bg-slate-950/90 z-0 border-t border-blue-900/30"></div>
          
          <div className="relative z-10 max-w-6xl mx-auto">
            <div className="mb-10 flex items-center gap-4 animate-on-scroll opacity-0 translate-y-12 transition-all duration-700 ease-out">
              <div className="h-8 w-1 bg-blue-500 rounded-full"></div>
              <h3 className="text-3xl font-extrabold text-white tracking-tight">Our Expertise</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {loading ? (
                [1, 2, 3].map((i) => (
                  <div key={i} className="p-6 rounded-2xl bg-slate-900/50 backdrop-blur-md animate-pulse border border-slate-800 h-40"></div>
                ))
              ) : (
                expertise.map((item, index) => (
                  <div 
                    key={item.id} 
                    style={{ transitionDelay: `${index * 150}ms` }}
                    className="relative overflow-hidden animate-on-scroll opacity-0 translate-y-12 transition-all duration-700 ease-out bg-slate-900/60 backdrop-blur-md border border-slate-800 rounded-2xl hover:bg-slate-800/80 hover:border-blue-500/50 hover:-translate-y-2 hover:shadow-[0_10px_30px_rgba(37,99,235,0.15)] group cursor-default"
                  >
                    <div className="absolute inset-0 z-0">
                      {item.bg_image && (
                        <img 
                          src={item.bg_image} 
                          className="w-full h-full object-cover mix-blend-screen transition-all duration-500 opacity-20 group-hover:opacity-40 blur-sm group-hover:blur-none" 
                          alt="" 
                        />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-br from-slate-950/70 via-slate-900/50 to-transparent"></div>
                    </div>
                    <div className="relative z-10 p-8 h-full flex flex-col">
                      <h4 className="text-xl font-bold text-white mb-3 group-hover:text-blue-400 transition-colors duration-300">{item.title}</h4>
                      <p className="text-slate-400 text-sm leading-relaxed group-hover:text-slate-300 transition-colors duration-300 flex-grow">{item.description}</p>
                      <div className="mt-6 h-[2px] w-8 bg-slate-700 group-hover:bg-blue-500 group-hover:w-16 transition-all duration-500"></div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </section>
      </div>

    </div>
  );
}

export default Home;